import React, { RefObject } from "react";
import styled, { keyframes } from "styled-components";
import RandomPromptIcon from "../../../assets/random-prompt.svg";
import CheckIcon from "../../../assets/instrumental-check.svg";
import CheckIconActive from "../../../assets/instrumental-check-active.svg";
import { DeviceSize } from "constants/deviceSize";

type EasyProps = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  songDescription: string;
  onDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isLyrics: boolean;
  setIsLyrics: (v: boolean) => void;
  isInstrumental: boolean;
  setIsInstrumental: (v: boolean) => void;
  availableInspirations: string[];
  onInspirationClick: (item: string) => void;
  promptMaxLength: number;
  className?: string;
  onGeneratePrompt?: () => void;
  isGeneratingPrompt?: boolean;
  onLyricsClick?: () => void;
  inspirationCount?: number;
  inspirationTotal?: number;
  onInspirationScrollEnd?: () => void;
};

export const LeftPanelEasy: React.FC<EasyProps> = ({
  textareaRef,
  songDescription,
  onDescriptionChange,
  isLyrics,
  setIsLyrics,
  isInstrumental,
  setIsInstrumental,
  availableInspirations,
  onInspirationClick,
  promptMaxLength,
  className,
  onGeneratePrompt,
  isGeneratingPrompt,
  onLyricsClick,
  inspirationCount,
  inspirationTotal,
  onInspirationScrollEnd,
}) => {
  return (
    <CombinedSection className={className}>
      <SongDescriptionCard>
        <CardHeader>
          <CardTitle>Song Description</CardTitle>
          <MiniIconButton
            type="button"
            disabled={isGeneratingPrompt}
            onClick={() => {
              if (isGeneratingPrompt) return;
              onGeneratePrompt?.();
            }}
          >
            {isGeneratingPrompt ? <IconSpinner /> : <StyledRandomPromptIcon />}
          </MiniIconButton>
        </CardHeader>
        <DescriptionInput
          as="textarea"
          ref={textareaRef}
          placeholder="Describe what you want the AI to create"
          value={songDescription}
          onChange={onDescriptionChange}
          maxLength={promptMaxLength}
        />
        <ActionButtons>
          <LeftButtons>
            <ActionButton 
              $active={isLyrics}
              $disabled={isInstrumental}
              onClick={() => {
                if (isInstrumental) return;
                setIsLyrics(true);
                onLyricsClick?.();
              }}
            >
              + Lyrics
            </ActionButton>
          </LeftButtons>
          <InstrumentalButton 
            $active={isInstrumental}
            onClick={() => {
              const nextValue = !isInstrumental;
              setIsInstrumental(nextValue);
              if (nextValue) {
                setIsLyrics(false);
              }
            }}
          >
            {isInstrumental ? <CheckIconActive /> : <CheckIcon />} Instrumental
          </InstrumentalButton>
        </ActionButtons>
      </SongDescriptionCard>

      <InspirationSeparator />

      <InspirationSection>
        <InspirationTitle>
          Inspiration
      
        </InspirationTitle>
        <InspirationScrollContainer
          onScroll={(event) => {
            const target = event.currentTarget;
            if (target.scrollWidth - target.scrollLeft - target.clientWidth <= 24) {
              onInspirationScrollEnd?.();
            }
          }}
        >
          {availableInspirations.map((inspiration) => (
            <InspirationButton 
              key={inspiration}
              onClick={() => onInspirationClick(inspiration)}
            >
              + {inspiration}
            </InspirationButton>
          ))}
        </InspirationScrollContainer>
      </InspirationSection>
    </CombinedSection>
  );
};

const CombinedSection = styled.div`
  background-color: #f4f4f4;
  border-radius: 20px;
  padding: 2.2vh 0 2.2vh 0;
  display: flex;
  flex-direction: column;
  gap: 1.2vh;
  margin: 2.2vh 0 0 0;
  max-width: 75%;
  @media (max-width: ${DeviceSize.TabletMedium}) {
   width: 122vw;
  }
`;

const SongDescriptionCard = styled.div`
  background-color: transparent;
  padding: 0px 20px 0px 20px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.8vh;
`;

const CardTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #333;
`;

const MiniIconButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#dfe3ff' : '#CFD3FF'};
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.7 : 1};
  
  &:hover {
    background-color: ${props => props.disabled ? '#dfe3ff' : '#b8bef5'};
  }

  @media (max-width: ${DeviceSize.TabletMedium}) {
    width: 40px;
    height: 40px;
  }
`;

const DescriptionInput = styled.input`
  width: 100%;
  min-height: 3vh;
  max-height: 23vh;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  font-size: 14px;
  color: #000;
  margin-bottom: 1.8vh;
  outline: none;
  resize: none;
  font-family: inherit;
  overflow-y: auto;
  &::placeholder {
    color: #999;
  }
`;

const InspirationSeparator = styled.div`
  height: 1px;
  background-color: #e0e0e0;
  margin: 0;
  margin: 0px 20px 0px 0px;

`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button<{ $active?: boolean; $disabled?: boolean }>`
  padding: 8px 12px;
  border-radius: 16px;
  border: none;
  background-color: #CFD3FF;
  color: #000;
  font-size: 12px;
  font-weight: 500;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.$disabled ? 0.4 : 1};
  
  &:hover {
    background-color: ${props => props.$disabled ? '#CFD3FF' : '#b8bef5'};
  }
`;

const InstrumentalButton = styled.button<{ $active: boolean }>`
  padding: 8px 12px;
  border-radius: 16px;
  border: 1px solid ${props => props.$active ? '#6777FF' : '#CFD3FF'};
  background-color: #f4f4f4;
  color: #000;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: ${props => props.$active ? 1 : 0.6};
  
  &:hover {
    background-color: #CFD3FF;
  }
  
  svg {
    width: 13px;
    height: 13px;
    transition: all 0.3s ease;
  }
`;

const InspirationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 0;
  max-width: 25vw;
`;

const InspirationTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #333;
  padding: 0px 0px 0px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InspirationScrollContainer = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 10px 8px 20px;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 2px;
    margin: 0 20px 0 20px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c0c0c0;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0a0a0;
  }
    @media (max-width: ${DeviceSize.TabletMedium}) {
   width: 90vw;
  }
`;

const InspirationButton = styled.button`
  padding: 8px 12px;
  border-radius: 16px;
  border: none;
  background-color: #CFD3FF;
  color: #000;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  max-width: 220px;
  white-space: normal;
  text-align: left;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-shrink: 0;
  
  &:hover {
    background-color: #b8bef5;
  }
`;

const StyledRandomPromptIcon = styled(RandomPromptIcon)`
  width: 15px;
  height: 15px;
  display: block;
`;

const CountBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #6777FF;
  background: rgba(103, 119, 255, 0.15);
  border-radius: 12px;
  padding: 2px 8px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const IconSpinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(103, 119, 255, 0.3);
  border-top-color: #6777FF;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;


export default LeftPanelEasy;


